import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalDialog,
  Box,
  FormControl,
  FormLabel,
  Input,
  FormHelperText,
  Typography,
  ModalClose,
  Button,
} from "@mui/joy";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { validateForm, validateInput } from "utils/validators";
import { ValidationModel } from "types/Validators";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

interface ChangeUserPasswordModalProps {
  open: boolean;
  userId: string;
  layout?: "center" | "fullscreen";
  onClose: () => void;
}

const ChangeUserPasswordModal: React.FC<ChangeUserPasswordModalProps> = ({
  open,
  userId,
  layout,
  onClose,
}) => {
  const [state, setState] = useState({
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState<{ [formFiel: string]: string[] }>({});
  const validationModel = useMemo<ValidationModel>(
    () => [
      {
        fieldName: "password",
        validators: {
          required: {
            value: true,
            message: "This field is required",
          },
          minLength: {
            value: 8,
            message: "The password must have min 8 chars",
          },
        },
      },
      {
        fieldName: "confirmPassword",
        validators: {
          required: {
            value: true,
            message: "This field is required",
          },
          equalTo: {
            value: "password",
            message: "Passwords did not match",
          },
        },
      },
    ],
    []
  );
  const [t] = useTranslation();
  const navigate = useNavigate();

  const mutatePassword = useCallback(async () => {
    try {
      const { data, status } = await axios.post<{ success: boolean }>(
        `/api/users/${userId}/change-password`,
        state
      );
      if (status === 200 && data.success) {
        toast.success(t("Password changed with success"));
        onClose();
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        //For the user logout if the request is unauthorized
        if (error.response?.status === 401) {
          navigate("/login");
        }
        console.log(error.response?.data);
        toast.error(error.response?.data.error);
      } else {
        console.log(error);
        toast.error(t("Something went wrong! Cannot change user's password"));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, userId, onClose, navigate]);

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    let validation = validateInput(
      e.currentTarget.name,
      e.currentTarget.value,
      validationModel,
      state
    );
    if (validation) {
      setErrors({ ...errors, [e.currentTarget.name]: validation.errors });
    }
  };

  const onSubmit = () => {
    const formValidation = validateForm(state, validationModel);
    if (formValidation.isValid) {
      mutatePassword();
    } else {
      setErrors(formValidation.errors);
    }
  };

  useEffect(() => {
    //Reset state whenever userid change
    setState({
      password: "",
      confirmPassword: "",
    });
  }, [userId]);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        sx={{
          minWidth: layout && layout === "fullscreen" ? undefined : 400,
          boxSizing: "border-box",
        }}
        layout={layout ? layout : "center"}
        size="lg"
        aria-labelledby="change-password-modal-title"
        aria-describedby="change-password-modal-description"
      >
        <Box mb={3} display="flex" alignItems={"center"}>
          <Typography level="h6" fontWeight="bold" lineHeight={1}>
            {t("Change User Password")}
          </Typography>
          <ModalClose />
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          width={"100%"}
          mb={3}
          rowGap={2}
          component={"form"}
          sx={(theme) => ({
            [theme.breakpoints.down("sm")]: {
              flex: "1 1",
            },
          })}
        >
          {/* Password         Confirm password */}
          <FormControl
            required
            sx={(theme) => ({
              flex: "0.4 1",
              [theme.breakpoints.down("sm")]: {
                flex: "unset",
              },
            })}
            error={"password" in errors && errors["password"].length > 0}
          >
            <FormLabel>{t("Password")}</FormLabel>
            <Input
              name="password"
              autoComplete="new-password"
              type="password"
              value={state.password}
              onChange={(e) =>
                setState({ ...state, password: e.currentTarget.value })
              }
              onBlur={onBlur}
            />
            {"password" in errors && errors["password"].length > 0
              ? errors["password"].map((m, i) => (
                  <FormHelperText key={i}>{m}</FormHelperText>
                ))
              : null}
          </FormControl>
          <FormControl
            required
            sx={(theme) => ({
              flex: "0.4 1",
              [theme.breakpoints.down("sm")]: {
                flex: "unset",
              },
            })}
            error={
              "confirmPassword" in errors &&
              errors["confirmPassword"].length > 0
            }
          >
            <FormLabel>{t("Confirm password")}</FormLabel>
            <Input
              name="confirmPassword"
              type="password"
              value={state.confirmPassword}
              onChange={(e) =>
                setState({ ...state, confirmPassword: e.currentTarget.value })
              }
              onBlur={onBlur}
            />
            {"confirmPassword" in errors && errors["confirmPassword"].length > 0
              ? errors["confirmPassword"].map((m, i) => (
                  <FormHelperText key={i}>{m}</FormHelperText>
                ))
              : null}
          </FormControl>
        </Box>
        <Box
          display="flex"
          alignItems={"center"}
          justifyContent={"flex-end"}
          gap={2}
          sx={(theme) => ({
            [theme.breakpoints.down("sm")]: {
              flexDirection: "column-reverse",
              width: "100%",
            },
          })}
        >
          <Button
            variant="plain"
            color="neutral"
            onClick={onClose}
            sx={(theme) => ({
              [theme.breakpoints.down("sm")]: {
                width: "100%",
              },
            })}
          >
            {t("Close")}
          </Button>
          <Button
            variant={layout && layout === "fullscreen" ? "solid" : "soft"}
            color="primary"
            onClick={onSubmit}
            sx={(theme) => ({
              [theme.breakpoints.down("sm")]: {
                width: "100%",
              },
            })}
          >
            {t("Change password")}
          </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
};

export default ChangeUserPasswordModal;
